import { useEffect, useMemo, useState } from "react";
import {
  Upload,
  Button,
  Table,
  message,
  Card,
  Row,
  Col,
  Empty,
  Typography,
  Badge,
  Progress,
  Space,
  Tabs,
  Spin,
  Alert,
  Select,
  Modal,
  Divider,
  Tooltip,
  Statistic,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  FileTextOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  CloseCircleOutlined,
  InboxOutlined,
} from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

// ==================== Utility Functions ====================
const toNumberIfPossible = (value) => {
  if (value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const getTypePieceId = (typePiece) => {
  if (!typePiece) return undefined;
  return toNumberIfPossible(
    typePiece.id ??
    typePiece.typePieceId ??
    typePiece.typeId ??
    typePiece.value ??
    typePiece.key
  );
};

const getTypePieceLabel = (typePiece) =>
  typePiece?.nom ||
  typePiece?.code ||
  typePiece?.libelleParametre ||
  typePiece?.label ||
  "Type de pièce";

const findTypePieceById = (collection = [], id) =>
  collection.find((item) => {
    const currentId = getTypePieceId(item);
    if (currentId === undefined || id === undefined) return false;
    if (
      typeof currentId === "number" &&
      typeof id === "number" &&
      !Number.isNaN(currentId) &&
      !Number.isNaN(id)
    ) {
      return currentId === id;
    }
    return String(currentId) === String(id);
  });

const sanitizeTypePieceParam = (value) => {
  if (value === null || value === undefined || value === "AUTRE") return undefined;
  return toNumberIfPossible(value);
};

const resolveTypePieceUploadParam = (collection = [], key) => {
  if (key === null || key === undefined) return undefined;
  const normalizedKey = toNumberIfPossible(key);
  const matched =
    findTypePieceById(collection, normalizedKey) ||
    findTypePieceById(collection, key);
  const candidate =
    matched?.code ||
    matched?.nom ||
    matched?.libelleParametre ||
    matched?.label ||
    normalizedKey ||
    key;
  if (candidate === null || candidate === undefined) return undefined;
  return String(candidate);
};

const getFileIcon = (fileName, contentType) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (contentType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) {
    return <FileImageOutlined style={{ fontSize: 48, color: '#52c41a' }} />;
  }
  if (contentType === 'application/pdf' || ext === 'pdf') {
    return <FilePdfOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />;
  }
  return <FileTextOutlined style={{ fontSize: 48, color: '#1890ff' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes) return 'N/A';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// ==================== API Mock Functions ====================
const mockUploadSinistrePiece = async (typeParam, file, sinistreId) => {
  await new Promise(resolve => setTimeout(resolve, 1500));
  return {
    id: Date.now(),
    url: URL.createObjectURL(file),
    downloadUrl: URL.createObjectURL(file),
    name: file.name,
    contentType: file.type,
  };
};

const mockUploadSinistreCapture = async (sinistreId, file) => {
  await new Promise(resolve => setTimeout(resolve, 1500));
  return {
    captureId: Date.now(),
    url: URL.createObjectURL(file),
    name: file.name,
    contentType: file.type,
  };
};

const mockTypePieces = [
  { id: 1, nom: "Pièce d'identité", code: "CNI" },
  { id: 2, nom: "Justificatif de domicile", code: "JD" },
  { id: 3, nom: "Certificat médical", code: "CM" },
  { id: 4, nom: "Devis de réparation", code: "DR" },
  { id: 5, nom: "Photos du sinistre", code: "PS" },
  { id: 6, nom: "Procès-verbal", code: "PV" },
];

// ==================== Custom Hooks ====================
const useTypePieces = () => {
  const [typePieces, setTypePieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTypePieces = async () => {
    setLoading(true);
    setError(null);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setTypePieces(mockTypePieces);
    } catch (err) {
      console.error("Erreur lors de la récupération des types de pièces:", err);
      setError(err.message);
      message.error("Impossible de charger les types de pièces");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypePieces();
  }, []);

  return { typePieces, loading, error, refetch: fetchTypePieces };
};

const buildCaptureEntry = (item, index = 0) => {
  if (!item) return null;
  const source = item.originFileObj || item.file || item;
  const uid = item.uid || item.captureId || item.id || `${Date.now()}-${index}`;
  const name = item.name || item.nom || item.fileName || (source && source.name) || `Capture-${index + 1}`;
  const url = item.url || item.link || item.preview || item.thumbUrl ||
    (item.base64 ? `data:${item.contentType || "image/png"};base64,${item.base64}` : undefined) ||
    (source && source.type && source.type.startsWith("image/") ? URL.createObjectURL(source) : undefined);
  const contentType = item.contentType || item.mimeType || (source && source.type) ||
    (name && name.toLowerCase().endsWith(".pdf") ? "application/pdf" : undefined);

  return {
    uid: uid.toString(),
    name,
    status: item.status || "done",
    url,
    thumbUrl: item.thumbUrl || url,
    contentType,
  };
};

// ==================== Main Component ====================
const DocumentsAFournir = ({
  sinistre = { id: 123, codeId: "SIN-2025-001" },
  captures = [],
  captured = [],
  uploadedPieces = [],
}) => {
  const sinistreId = sinistre?.codeId || sinistre?.id;
  const { typePieces, loading: loadingTypes, error: errorTypes, refetch } = useTypePieces();

  const [fileLists, setFileLists] = useState({});
  const [captureList, setCaptureList] = useState([]);

  // États pour la prévisualisation des captures
  const [pendingCaptures, setPendingCaptures] = useState([]);
  const [capturePreviewModal, setCapturePreviewModal] = useState(false);
  const [uploadingCaptures, setUploadingCaptures] = useState(false);

  // États pour la prévisualisation des pièces
  const [pendingPieces, setPendingPieces] = useState([]);
  const [piecePreviewModal, setPiecePreviewModal] = useState(false);
  const [uploadingPieces, setUploadingPieces] = useState(false);

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [activeTab, setActiveTab] = useState("captures");
  const [caps, setCaps] = useState([]);

  useEffect(() => {
    if (Array.isArray(captured) && captured.length) {
      setCaps(captured.map((item, index) => buildCaptureEntry(item, index)).filter(Boolean));
    } else if (Array.isArray(captures) && captures.length) {
      setCaps(captures.map((item, index) => buildCaptureEntry(item, index)).filter(Boolean));
    } else {
      setCaps([]);
    }
  }, [captures, captured]);

  // ==================== Handlers pour Captures ====================
  const handleCaptureBeforeUpload = (file) => {
    const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
    if (!isValidType) {
      message.error('Vous ne pouvez téléverser que des images ou des PDF');
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Le fichier doit être inférieur à 10 MB');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleCaptureChange = ({ fileList }) => {
    setPendingCaptures(fileList);
  };

  const handleCaptureRemovePreview = (file) => {
    setPendingCaptures(prev => prev.filter(item => item.uid !== file.uid));
  };

  const handleOpenCapturePreview = () => {
    if (pendingCaptures.length === 0) {
      message.warning('Veuillez sélectionner au moins un document');
      return;
    }
    setCapturePreviewModal(true);
  };

  const handleConfirmCaptureUpload = async () => {
    if (!sinistreId) {
      message.error("Impossible d'associer les captures au sinistre");
      return;
    }

    setUploadingCaptures(true);
    try {
      const uploadPromises = pendingCaptures.map(async (fileItem) => {
        const file = fileItem.originFileObj;
        const response = await mockUploadSinistreCapture(sinistreId, file);
        return {
          uid: response.captureId?.toString() || Date.now().toString(),
          name: file.name,
          status: "done",
          url: response.url,
          thumbUrl: response.url,
          contentType: file.type,
          size: file.size,
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setCaptureList(prev => [...prev, ...uploadedFiles]);
      setPendingCaptures([]);
      setCapturePreviewModal(false);
      message.success(`${uploadedFiles.length} document(s) ajouté(s) avec succès`);
    } catch (err) {
      console.error(err);
      message.error(`Échec de l'ajout des documents: ${err.message}`);
    } finally {
      setUploadingCaptures(false);
    }
  };

  const handleCaptureRemove = (file) => {
    Modal.confirm({
      title: "Supprimer cette capture ?",
      content: `Êtes-vous sûr de vouloir supprimer "${file.name}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => {
        setCaptureList(prev => prev.filter(item => item.uid !== file.uid));
        message.success("Capture supprimée");
      },
    });
  };

  // ==================== Handlers pour Pièces ====================
  const handlePieceBeforeUpload = (file, typePieceId) => {
    const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
    if (!isValidType) {
      message.error('Vous ne pouvez téléverser que des images ou des PDF');
      return Upload.LIST_IGNORE;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Le fichier doit être inférieur à 10 MB');
      return Upload.LIST_IGNORE;
    }

    setPendingPieces(prev => [
      ...prev,
      {
        file,
        typePieceId,
        uid: file.uid,
        name: file.name,
        size: file.size,
        type: file.type,
      }
    ]);

    return false;
  };

  const handleOpenPiecePreview = () => {
    if (pendingPieces.length === 0) {
      message.warning('Veuillez sélectionner au moins un document');
      return;
    }
    setPiecePreviewModal(true);
  };

  const handleRemovePendingPiece = (uid) => {
    setPendingPieces(prev => prev.filter(item => item.uid !== uid));
  };

  const handleConfirmPieceUpload = async () => {
    setUploadingPieces(true);
    try {
      const uploadPromises = pendingPieces.map(async (item) => {
        const uploadTypeParam = resolveTypePieceUploadParam(typePieces, item.typePieceId);
        if (!uploadTypeParam) {
          throw new Error(`Type de pièce invalide pour ${item.name}`);
        }

        const response = await mockUploadSinistrePiece(uploadTypeParam, item.file, sinistreId);
        return {
          typePieceId: item.typePieceId,
          file: {
            uid: response.id?.toString() || Date.now().toString(),
            name: item.name,
            status: "done",
            url: response.url,
            downloadUrl: response.downloadUrl,
            contentType: item.type,
            size: item.size,
          }
        };
      });

      const uploadedItems = await Promise.all(uploadPromises);

      setFileLists(prev => {
        const newLists = { ...prev };
        uploadedItems.forEach(({ typePieceId, file }) => {
          newLists[typePieceId] = [...(newLists[typePieceId] || []), file];
        });
        return newLists;
      });

      setPendingPieces([]);
      setPiecePreviewModal(false);
      message.success(`${uploadedItems.length} pièce(s) téléversée(s) avec succès`);
    } catch (err) {
      console.error(err);
      message.error(`Échec du téléversement: ${err.message}`);
    } finally {
      setUploadingPieces(false);
    }
  };

  const handleRemovePiece = (typePieceId, file) => {
    Modal.confirm({
      title: "Supprimer ce fichier ?",
      content: `Êtes-vous sûr de vouloir supprimer "${file.name}" ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => {
        setFileLists(prev => ({
          ...prev,
          [typePieceId]: (prev[typePieceId] || []).filter(f => f.uid !== file.uid),
        }));
        message.success("Fichier supprimé");
      },
    });
  };

  // ==================== Preview Handlers ====================
  const handlePreview = (item) => {
    if (item.url?.startsWith("data:image/") || item.type?.startsWith('image/')) {
      setPreviewImage(item.url || URL.createObjectURL(item.originFileObj));
      setPreviewTitle(item.name);
      setPreviewVisible(true);
    } else if (item.url) {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownload = (item) => {
    if (item.url) {
      const link = document.createElement("a");
      link.href = item.url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(`Téléchargement de ${item.name}`);
    }
  };

  // ==================== Computed Values ====================
  const summaryData = useMemo(() => {
    return Object.entries(fileLists).flatMap(([typeId, files]) => {
      const parsed = Number(typeId);
      const lookupId = Number.isNaN(parsed) ? typeId : parsed;
      const typePiece = findTypePieceById(typePieces, lookupId);
      return (files || []).map((f) => ({
        key: f.uid,
        typePieceId: typeId,
        label: getTypePieceLabel(typePiece) || `Type ${typeId}`,
        name: f.name,
        size: formatFileSize(f.size),
      }));
    });
  }, [fileLists, typePieces]);

  const completedCount = useMemo(() => {
    return typePieces.filter((tp) => {
      const id = getTypePieceId(tp);
      return (fileLists[id]?.length || 0) > 0;
    }).length;
  }, [fileLists, typePieces]);

  const totalCount = typePieces.length;
  const completion = totalCount === 0 ? 0 : Math.round((completedCount / totalCount) * 100);

  // ==================== Loading & Error States ====================
  if (loadingTypes) {
    return (
      <div style={{ padding: 60, textAlign: "center" }}>
        <Spin size="large" tip="Chargement des types de pièces..." />
      </div>
    );
  }

  if (errorTypes) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Erreur de chargement"
          description={`Impossible de charger les types de pièces: ${errorTypes}`}
          type="error"
          showIcon
          action={
            <Button size="small" danger onClick={refetch} icon={<ReloadOutlined />}>
              Réessayer
            </Button>
          }
        />
      </div>
    );
  }

  // ==================== Render ====================
  const tabItems = [
    {
      key: "captures",
      label: (
        <Space>
          <PictureOutlined />
          <span>Documents fournis</span>
          <Badge count={caps.length} showZero style={{ backgroundColor: '#1890ff' }} />
        </Space>
      ),
      children: (
        <div style={{ padding: '24px 0' }}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #242166 100%)',
                  color: 'white',
                  height: '100%',
                }}
              >
                <Row gutter={16} align="middle">
                  <Col flex="auto">
                    <Title level={3} style={{ color: 'white', marginTop: "10px" }}>
                      Documents et captures
                    </Title>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.85)', margin: '8px 0 0 0' }}>
                      Gérez tous les documents liés à ce dossier sinistre
                    </Paragraph>
                  </Col>
                  <Col>
                    <Statistic
                      title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total</span>}
                      value={caps.length}
                      valueStyle={{ color: 'white', fontSize: 32 }}
                      prefix={<FileTextOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={24}>
              <Card
                title={
                  <Space>
                    <CloudUploadOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                    <span>Ajouter de nouveaux documents</span>
                  </Space>
                }
                extra={
                  pendingCaptures.length > 0 && (
                    <Badge count={pendingCaptures.length} style={{ backgroundColor: '#52c41a' }}>
                      <Button
                        type="primary"
                        icon={<EyeOutlined />}
                        onClick={handleOpenCapturePreview}
                      >
                        Prévisualiser et enregistrer
                      </Button>
                    </Badge>
                  )
                }
              >
                <Dragger
                  multiple
                  beforeUpload={handleCaptureBeforeUpload}
                  onChange={handleCaptureChange}
                  fileList={pendingCaptures}
                  onRemove={handleCaptureRemovePreview}
                  accept=".png,.jpg,.jpeg,.webp,.bmp,.gif,.pdf"
                  listType="picture"
                  onPreview={handlePreview}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text">
                    Cliquez ou glissez-déposez vos fichiers ici
                  </p>
                  <p className="ant-upload-hint">
                    Formats acceptés : Images (PNG, JPG, JPEG, WEBP) et PDF. Taille max : 10 MB
                  </p>
                </Dragger>
              </Card>
            </Col>

            <Col span={24}>
              <Divider orientation="left">
                <Space>
                  <FolderOpenOutlined />
                  Documents enregistrés ({caps?.length})
                </Space>
              </Divider>
            </Col>

            {caps?.length > 0 ? (
              caps?.map((capture, index) => (
                <Col key={capture.uid || index} xs={24} sm={12} md={8} lg={6}>
                  <Card
                    hoverable
                    bodyStyle={{ padding: 12 }}
                    cover={
                      capture.url?.startsWith("data:image/") ? (
                        <div
                          style={{
                            height: 180,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            cursor: 'pointer',
                          }}
                          onClick={() => handlePreview(capture)}
                        >
                          <img
                            src={capture.url}
                            alt={capture.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            height: 180,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                          }}
                        >
                          {getFileIcon(capture.name, capture.contentType)}
                        </div>
                      )
                    }
                    actions={[
                      <Tooltip title="Télécharger">
                        <DownloadOutlined
                          key="download"
                          onClick={() => handleDownload(capture)}
                        />
                      </Tooltip>,
                      <Tooltip title="Supprimer">
                        <DeleteOutlined
                          key="delete"
                          style={{ color: '#ff4d4f' }}
                          onClick={() => handleCaptureRemove(capture)}
                        />
                      </Tooltip>,
                    ]}
                  >
                    <Paragraph
                      ellipsis={{ rows: 2 }}
                      style={{ marginBottom: 4, fontWeight: 500 }}
                      title={capture.name}
                    >
                      {capture.name}
                    </Paragraph>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatFileSize(capture.size)}
                    </Text>
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Aucun document enregistré"
                  style={{ padding: '60px 0' }}
                />
              </Col>
            )}

            {summaryData.length > 0 && (
              <Col span={24}>
                <Card title="Récapitulatif des documents par type">
                  <Table
                    dataSource={summaryData}
                    columns={[
                      { title: "Type de pièce", dataIndex: "label", key: "label" },
                      { title: "Nom du fichier", dataIndex: "name", key: "name" },
                      { title: "Taille", dataIndex: "size", key: "size", width: 120 },
                    ]}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </Card>
              </Col>
            )}
          </Row>
        </div>
      ),
    },
    {
      key: "required",
      label: (
        <Space>
          <FolderOpenOutlined />
          <span>Pièces obligatoires</span>
          <Badge count={`${completedCount}/${totalCount}`} style={{ backgroundColor: completion === 100 ? '#52c41a' : '#faad14' }} />
        </Space>
      ),
      children: (
        <div style={{ padding: '24px 0' }}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card
                bordered={false}
                style={{
                  background: 'linear-gradient(135deg, #96a7f5ff 0%, #242166 100%)',
                  color: 'white',
                }}
              >
                <Row gutter={24} align="middle">
                  <Col>
                    <Progress
                      type="circle"
                      percent={completion}
                      width={100}
                      strokeColor={{
                        '0%': '#108ee9',
                        '100%': '#87d068',
                      }}
                    />
                  </Col>
                  <Col flex="auto">
                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                      Progression des documents obligatoires
                    </Title>
                    <Paragraph style={{ color: 'rgba(255,255,255,0.85)', margin: '8px 0 0 0', fontSize: 16 }}>
                      {completedCount} sur {totalCount} pièces fournies
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>

            {pendingPieces.length > 0 && (
              <Col span={24}>
                <Alert
                  message={`${pendingPieces.length} fichier(s) en attente d'enregistrement`}
                  type="info"
                  showIcon
                  action={
                    <Button type="primary" onClick={handleOpenPiecePreview}>
                      Prévisualiser et enregistrer
                    </Button>
                  }
                />
              </Col>
            )}

            <Col span={24}>
              <Divider orientation="left">
                <Space>
                  <FileTextOutlined />
                  Liste des pièces requises
                </Space>
              </Divider>
            </Col>

            {typePieces.map((typePiece) => {
              const typePieceId = getTypePieceId(typePiece);
              const typePieceKey = typePieceId ?? "AUTRE";
              const files = fileLists[typePieceKey] || [];
              const hasFiles = files.length > 0;
              const pendingCount = pendingPieces.filter(p => p.typePieceId === typePieceId).length;

              return (
                <Col key={typePieceKey ?? typePiece.id} xs={24} sm={12} lg={8}>
                  <Badge.Ribbon
                    text={hasFiles ? "Complet" : "Manquant"}
                    color={hasFiles ? "green" : "red"}
                  >
                    <Card
                      hoverable
                      style={{ height: '100%', minHeight: 280 }}
                      title={
                        <Space>
                          {hasFiles ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                          ) : (
                            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
                          )}
                          <span>{getTypePieceLabel(typePiece)}</span>
                        </Space>
                      }
                      extra={
                        pendingCount > 0 && (
                          <Badge count={pendingCount} style={{ backgroundColor: '#1890ff' }} />
                        )
                      }
                    >
                      <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <div style={{ minHeight: 120 }}>
                          {files.length > 0 ? (
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              {files.map((f) => (
                                <div
                                  key={f.uid}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '8px 12px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 6,
                                    border: '1px solid #e8e8e8',
                                  }}
                                >
                                  <Space>
                                    {getFileIcon(f.name, f.contentType)}
                                    <div>
                                      <Text
                                        ellipsis
                                        style={{ maxWidth: 150, display: 'block', fontWeight: 500 }}
                                        title={f.name}
                                      >
                                        {f.name}
                                      </Text>
                                      <Text type="secondary" style={{ fontSize: 11 }}>
                                        {formatFileSize(f.size)}
                                      </Text>
                                    </div>
                                  </Space>
                                  <Button
                                    type="text"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleRemovePiece(typePieceKey, f)}
                                  />
                                </div>
                              ))}
                            </Space>
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="Aucun fichier"
                              style={{ margin: '20px 0' }}
                            />
                          )}
                        </div>

                        <Upload
                          showUploadList={false}
                          beforeUpload={(file) => {
                            if (typePieceId === undefined) {
                              message.warning("Impossible d'identifier ce type de pièce");
                              return Upload.LIST_IGNORE;
                            }
                            return handlePieceBeforeUpload(file, typePieceId);
                          }}
                          accept=".png,.jpg,.jpeg,.pdf"
                          multiple
                        >
                          <Button
                            icon={<UploadOutlined />}
                            block
                            size="large"
                            type={hasFiles ? "default" : "primary"}
                            disabled={typePieceId === undefined}
                          >
                            {hasFiles ? "Ajouter un fichier" : "Téléverser"}
                          </Button>
                        </Upload>
                      </Space>
                    </Card>
                  </Badge.Ribbon>
                </Col>
              );
            })}
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div style={{ background: '#f0f2f5', minHeight: '100vh', padding: '24px' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
          tabBarStyle={{
            marginBottom: 0,
            background: 'white',
            padding: '0 24px',
            borderRadius: '8px 8px 0 0',
          }}
        />
      </div>

      {/* Modal de prévisualisation des captures */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>Prévisualisation des documents à enregistrer</span>
          </Space>
        }
        open={capturePreviewModal}
        onCancel={() => {
          if (!uploadingCaptures) {
            setCapturePreviewModal(false);
          }
        }}
        width={900}
        footer={[
          <Button
            key="cancel"
            onClick={() => setCapturePreviewModal(false)}
            disabled={uploadingCaptures}
          >
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={handleConfirmCaptureUpload}
            loading={uploadingCaptures}
            disabled={pendingCaptures.length === 0}
          >
            Enregistrer {pendingCaptures.length} document(s)
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message={`${pendingCaptures.length} fichier(s) prêt(s) à être enregistré(s)`}
            type="info"
            showIcon
          />

          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              {pendingCaptures.map((file, index) => (
                <Col key={file.uid} span={12}>
                  <Card
                    size="small"
                    hoverable
                    bodyStyle={{ padding: 12 }}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      <div
                        style={{
                          height: 120,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fafafa',
                          borderRadius: 4,
                          overflow: 'hidden',
                        }}
                      >
                        {file.type?.startsWith('image/') ? (
                          <img
                            src={URL.createObjectURL(file.originFileObj)}
                            alt={file.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          getFileIcon(file.name, file.type)
                        )}
                      </div>
                      <div>
                        <Paragraph
                          ellipsis={{ rows: 1 }}
                          strong
                          style={{ marginBottom: 4 }}
                          title={file.name}
                        >
                          {file.name}
                        </Paragraph>
                        <Space split="|" size="small">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatFileSize(file.size)}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {file.type}
                          </Text>
                        </Space>
                      </div>
                      <Button
                        danger
                        size="small"
                        block
                        icon={<CloseCircleOutlined />}
                        onClick={() => handleCaptureRemovePreview(file)}
                        disabled={uploadingCaptures}
                      >
                        Retirer
                      </Button>
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Space>
      </Modal>

      {/* Modal de prévisualisation des pièces */}
      <Modal
        title={
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <span>Prévisualisation des pièces à enregistrer</span>
          </Space>
        }
        open={piecePreviewModal}
        onCancel={() => {
          if (!uploadingPieces) {
            setPiecePreviewModal(false);
          }
        }}
        width={900}
        footer={[
          <Button
            key="cancel"
            onClick={() => setPiecePreviewModal(false)}
            disabled={uploadingPieces}
          >
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<CloudUploadOutlined />}
            onClick={handleConfirmPieceUpload}
            loading={uploadingPieces}
            disabled={pendingPieces.length === 0}
          >
            Enregistrer {pendingPieces.length} pièce(s)
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Alert
            message={`${pendingPieces.length} fichier(s) prêt(s) à être enregistré(s)`}
            type="info"
            showIcon
          />

          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {pendingPieces.map((item) => {
                const typePiece = findTypePieceById(typePieces, item.typePieceId);
                return (
                  <Card
                    key={item.uid}
                    size="small"
                    hoverable
                    style={{ borderLeft: '4px solid #1890ff' }}
                  >
                    <Row gutter={16} align="middle">
                      <Col flex="80px">
                        <div
                          style={{
                            width: 70,
                            height: 70,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                            borderRadius: 4,
                          }}
                        >
                          {getFileIcon(item.name, item.type)}
                        </div>
                      </Col>
                      <Col flex="auto">
                        <Space direction="vertical" size={2} style={{ width: '100%' }}>
                          <Text strong ellipsis title={item.name}>
                            {item.name}
                          </Text>
                          <Space size="small">
                            <Badge
                              color="blue"
                              text={
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  {getTypePieceLabel(typePiece)}
                                </Text>
                              }
                            />
                          </Space>
                          <Space split="|" size="small">
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {formatFileSize(item.size)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {item.type}
                            </Text>
                          </Space>
                        </Space>
                      </Col>
                      <Col>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemovePendingPiece(item.uid)}
                          disabled={uploadingPieces}
                        >
                          Retirer
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                );
              })}
            </Space>
          </div>
        </Space>
      </Modal>

      {/* Modal de prévisualisation d'image */}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="80%"
        style={{ top: 20 }}
        zIndex={2000}
      >
        <img alt={previewTitle} style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default DocumentsAFournir;